import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import type { ServiceEnterpriseDraft } from '@super-mega/server/contracts/order'
import { useTRPC } from '@/trpc/react.ts'
import { EnterpriseEditorModal } from './enterprise-editor-modal.tsx'

export function EnterpriseCreateModal({
  open,
  onOpenChange,
  initialDraft,
  onSuccess
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialDraft?: ServiceEnterpriseDraft
  onSuccess?: () => void
}) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message } = App.useApp()
  const [draft, setDraft] = useState<ServiceEnterpriseDraft>(
    initialDraft ?? { customerId: 0, kind: 'prospectiveCompany', displayName: '' }
  )
  const [generation, setGeneration] = useState(0)
  const [wasOpen, setWasOpen] = useState(open)
  const submission = useRef<{ payload: string; key: string; generation: number } | undefined>(undefined)
  const mutation = useMutation(trpc.order.subjects.create.mutationOptions())
  // 每次打开都是新建会话；关闭时保留节点，让退出动画完成。
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setDraft(initialDraft ?? { customerId: 0, kind: 'prospectiveCompany', displayName: '' })
      setGeneration((previous) => previous + 1)
    }
  }
  return (
    <EnterpriseEditorModal
      key={generation}
      open={open}
      onOpenChange={onOpenChange}
      value={draft}
      onChange={setDraft}
      pending={mutation.isPending}
      submitText="保存服务对象"
      onConfirm={async (value) => {
        const payload = JSON.stringify(value)
        if (submission.current?.payload !== payload || submission.current.generation !== generation) {
          submission.current = { payload, key: crypto.randomUUID(), generation }
        }
        await mutation.mutateAsync({ submissionKey: submission.current.key, draft: value })
        await Promise.all([
          cache.invalidateQueries(trpc.order.subjects.list.queryFilter()),
          cache.invalidateQueries(trpc.order.subjects.options.queryFilter()),
          cache.invalidateQueries(trpc.order.subjects.detail.queryFilter()),
          cache.invalidateQueries(trpc.company.reusable.queryFilter()),
          cache.invalidateQueries(trpc.company.search.queryFilter()),
          cache.invalidateQueries(trpc.identity.reusable.queryFilter()),
          cache.invalidateQueries(trpc.identity.search.queryFilter()),
          cache.invalidateQueries(trpc.license.list.queryFilter())
        ])
        onOpenChange(false)
        onSuccess?.()
        submission.current = undefined
        void message.success('服务对象已保存')
        return true
      }}
    />
  )
}
